/**
 * SessionManager - Orchestrates authentication state synchronization
 * Manages user session lifecycle between Cognito and application state
 */

import { storageService, STORAGE_KEYS } from '../storage/StorageService';
import { userProfileService } from '../api/UserProfileService';
import type { UserProfile } from '../../types/user';
import type { JWTPayload } from '../../types/auth';

/**
 * Session state interface
 */
export interface SessionState {
  isInitialized: boolean;
  profile: UserProfile | null;
  error: Error | null;
}

/**
 * SessionManager class for managing user session lifecycle
 * 
 * Responsibilities:
 * - Initialize user session on app load
 * - Sync user profile after authentication
 * - Handle token expiration
 * - Clear session on sign out
 */
export class SessionManager {
  private sessionState: SessionState = {
    isInitialized: false,
    profile: null,
    error: null,
  };

  private tokenExpirationCheckInterval: number | null = null;
  private readonly TOKEN_CHECK_INTERVAL = 60000; // Check every minute

  /**
   * Initialize session on app load
   * Loads authentication state and syncs user profile if authenticated
   * 
   * @returns Promise that resolves when initialization is complete
   */
  async initializeSession(): Promise<void> {
    try {
      // Check if user has valid tokens
      const idToken = storageService.getItem(STORAGE_KEYS.ID_TOKEN);
      const accessToken = storageService.getItem(STORAGE_KEYS.ACCESS_TOKEN);

      if (!idToken || !accessToken) {
        // No tokens, user not authenticated
        this.sessionState = {
          isInitialized: true,
          profile: null,
          error: null,
        };
        return;
      }

      // Validate token expiration
      if (!this.isTokenValid(idToken)) {
        // Token expired, clear session
        this.clearSession();
        this.sessionState = {
          isInitialized: true,
          profile: null,
          error: new Error('Session expired'),
        };
        return;
      }

      // Sync user profile with backend
      const profile = await this.syncUserProfile(idToken);

      this.sessionState = {
        isInitialized: true,
        profile,
        error: null,
      };

      // Start automatic token expiration checking
      this.startTokenExpirationCheck();
    } catch (error) {
      console.error('[SessionManager] Failed to initialize session', error);
      this.sessionState = {
        isInitialized: true,
        profile: null,
        error: error as Error,
      };
    }
  }

  /**
   * Sync user profile with backend after authentication
   * Creates new user record or updates existing one
   * 
   * @param idToken - Cognito ID token (JWT)
   * @returns User profile data
   * @throws Error if sync fails
   */
  async syncUserProfile(idToken: string): Promise<UserProfile> {
    try {
      // Validate token before syncing
      if (!this.isTokenValid(idToken)) {
        throw new Error('Cannot sync with expired token');
      }

      // Call user sync API
      const profile = await userProfileService.syncUser(idToken);

      // Update session state
      this.sessionState.profile = profile;
      this.sessionState.error = null;

      return profile;
    } catch (error) {
      console.error('[SessionManager] Failed to sync user profile', error);
      this.sessionState.error = error as Error;
      throw error;
    }
  }

  /**
   * Refresh session by reloading user profile
   * Fetches latest profile data from backend
   * 
   * @returns Promise that resolves when refresh is complete
   * @throws Error if not authenticated or refresh fails
   */
  async refreshSession(): Promise<void> {
    try {
      // Check if user is authenticated
      const idToken = storageService.getItem(STORAGE_KEYS.ID_TOKEN);
      
      if (!idToken) {
        throw new Error('Cannot refresh session: not authenticated');
      }

      // Validate token
      if (!this.isTokenValid(idToken)) {
        // Token expired, clear session
        this.clearSession();
        throw new Error('Cannot refresh session: token expired');
      }

      // Fetch latest profile
      const profile = await userProfileService.getProfile();

      // Update session state
      this.sessionState.profile = profile;
      this.sessionState.error = null;
    } catch (error) {
      console.error('[SessionManager] Failed to refresh session', error);
      this.sessionState.error = error as Error;
      throw error;
    }
  }

  /**
   * Clear session and remove all user data
   * Called on sign out or token expiration
   */
  clearSession(): void {
    try {
      // Clear tokens from storage
      storageService.removeItem(STORAGE_KEYS.ID_TOKEN);
      storageService.removeItem(STORAGE_KEYS.ACCESS_TOKEN);

      // Clear session state
      this.sessionState = {
        isInitialized: true,
        profile: null,
        error: null,
      };

      // Stop token expiration checking
      this.stopTokenExpirationCheck();
    } catch (error) {
      console.error('[SessionManager] Failed to clear session', error);
    }
  }

  /**
   * Check if current session is valid
   * Validates token expiration
   * 
   * @returns True if session is valid
   */
  isSessionValid(): boolean {
    const idToken = storageService.getItem(STORAGE_KEYS.ID_TOKEN);
    
    if (!idToken) {
      return false;
    }

    return this.isTokenValid(idToken);
  }

  /**
   * Get current session state
   * 
   * @returns Current session state
   */
  getSessionState(): SessionState {
    return { ...this.sessionState };
  }

  /**
   * Get current user profile
   * 
   * @returns User profile or null if not authenticated
   */
  getProfile(): UserProfile | null {
    return this.sessionState.profile;
  }

  /**
   * Check if token is valid (not expired)
   * 
   * @param token - JWT token string
   * @returns True if token is valid
   */
  private isTokenValid(token: string): boolean {
    try {
      const payload = this.decodeJWT(token);
      
      if (!payload) {
        return false;
      }

      // Check expiration
      const now = Math.floor(Date.now() / 1000);
      return payload.exp > now;
    } catch (error) {
      console.error('[SessionManager] Failed to validate token', error);
      return false;
    }
  }

  /**
   * Decode JWT token
   * 
   * @param token - JWT token string
   * @returns Decoded payload or null
   */
  private decodeJWT(token: string): JWTPayload | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }

      const payload = parts[1];
      if (!payload) {
        return null;
      }

      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decoded);
    } catch (error) {
      console.error('[SessionManager] Failed to decode JWT', error);
      return null;
    }
  }

  /**
   * Start automatic token expiration checking
   * Checks token validity periodically and clears session if expired
   */
  private startTokenExpirationCheck(): void {
    // Clear any existing interval
    this.stopTokenExpirationCheck();

    // Set up periodic check
    this.tokenExpirationCheckInterval = window.setInterval(() => {
      if (!this.isSessionValid()) {
        console.warn('[SessionManager] Token expired, clearing session');
        this.clearSession();
        
        // Optionally trigger a callback or event here
        // For now, just clear the session
      }
    }, this.TOKEN_CHECK_INTERVAL);
  }

  /**
   * Stop automatic token expiration checking
   */
  private stopTokenExpirationCheck(): void {
    if (this.tokenExpirationCheckInterval !== null) {
      clearInterval(this.tokenExpirationCheckInterval);
      this.tokenExpirationCheckInterval = null;
    }
  }

  /**
   * Cleanup method to be called when SessionManager is no longer needed
   * Stops all background processes
   */
  cleanup(): void {
    this.stopTokenExpirationCheck();
  }
}

// Export singleton instance
export const sessionManager = new SessionManager();
